import * as CSS from 'csstype';
import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { SortableList, } from './sortableList';
import { DragHandle, } from './dragHandle'
import { Tooltip } from './tooltip';
import Checkbox from './checkBox';
import ActionMenu from './actionMenu';

import { helpIcon } from '../../images';
import { DISABLED, EXTRA_SMALL_ICON_BASE_SRC, INDETERMINATE, SMALL_ICON_BASE_SRC } from '../../utils/constants';

export const descending = 'descending'
export const ascending = 'ascending'

const dataTableHeaderClassName = 'data-table-header'
const NO_DATA = '—'

const left: CSS.TextAlignProperty = "left"
const right: CSS.TextAlignProperty = "right"

// Helper to extract plain text from React elements (for aria-labels)
const extractTextFromReactElement = (element: any): string => {
  if (typeof element === 'string') return element;
  if (typeof element === 'number') return String(element);
  if (!element) return '';
  if (Array.isArray(element)) return element.map(extractTextFromReactElement).join('');
  if (element.props?.children) return extractTextFromReactElement(element.props.children);
  return '';
};

// Simple HTML tags safe to render with renderToStaticMarkup (no hooks/context)
const SIMPLE_HTML_TAGS = ['em', 'i', 'strong', 'b', 'u', 'span', 'br'];

// Helper to convert React elements to HTML string (preserves formatting for tooltips)
// Only renders simple HTML elements; complex components fall back to text extraction
const reactElementToHtml = (element: any): string => {
  if (typeof element === 'string') return element;
  if (typeof element === 'number') return String(element);
  if (!element) return '';
  if (Array.isArray(element)) return element.map(reactElementToHtml).join('');
  if (React.isValidElement(element)) {
    const type = (element as React.ReactElement).type;
    // Only use renderToStaticMarkup for simple HTML tags, not components with hooks
    if (typeof type === 'string' && SIMPLE_HTML_TAGS.includes(type)) {
      return renderToStaticMarkup(element);
    }
    // For complex components, extract text instead
    return extractTextFromReactElement(element);
  }
  return '';
};

const removeSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/remove.svg`
const sortChevronUpSrc = `${SMALL_ICON_BASE_SRC}/collapse.svg`
const sortChevronDownSrc = `${SMALL_ICON_BASE_SRC}/expand.svg`
const reorderSrc = `${window.postbuildEnv.CDN_URL}/images/icons/reorder.svg`
const extraSmallReorderSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/reorder.svg`
const toggleArrowExpandedSrc = `${window.postbuildEnv.CDN_URL}/images/icons/data_table/toggle-arrow-expanded.svg`
const toggleArrowClosedSrc = `${window.postbuildEnv.CDN_URL}/images/icons/data_table/toggle-arrow-closed.svg`
const aggregateRowArrowSrc = `${window.postbuildEnv.CDN_URL}/images/icons/data_table/aggregate-row-arrow.svg`

interface DataTableRow {
  id: number | string;
  onClick?: Function;
  className?: string;
  actions?: Array<{ name?: string, action?: Function, element?: JSX.Element }>
  [key: string]: any;
}

interface DataTableHeader {
  width: string;
  name: string | React.ReactElement;
  attribute: string;
  noTooltip?: boolean;
  rowSectionClassName?: string;
  headerClassName?: string;
  isSortable?: boolean;
  ariaLabel?: string;
  renderAsCell?: boolean;
  renderCellsAsHeaders?: boolean;
}

interface DataTableProps {
  headers: Array<DataTableHeader>;
  rows: Array<DataTableRow>;
  averageFontWidth?: number;
  className?: string;
  defaultSortAttribute?: string;
  defaultSortDirection?: string;
  emptyStateMessage?: string;
  showCheckboxes?: boolean;
  showRemoveIcon?: boolean;
  removeHeaderText?: string;
  showActions?: boolean;
  removeRow?: (event: any) => void;
  checkRow?: (event: any) => void;
  uncheckRow?: (event: any) => void;
  uncheckAllRows?: (event: any) => void;
  checkAllRows?: (event: any) => void;
  sortIsDisabled?: boolean;
  isReorderable?: boolean;
  reorderCallback?: (event: any) => void;
  reportingFormat?: boolean;
  tableAriaLabel?: string;
  overlayInPortal?: boolean;
  overlayWrapper?: (children: any) => any;
  tableRef?: any;
}

interface DataTableState {
  sortAttribute?: string;
  sortAscending?: boolean;
  rowWithActionsOpen?: number | string;
  expandedParentRowIdentifier?: string;
  hoveredColIndex: null | number;
}

export class DataTable extends React.Component<DataTableProps, DataTableState> {
  static defaultProps: { averageFontWidth: number }  // eslint-disable-line react/sort-comp

  constructor(props) {
    super(props)

    this.state = {
      sortAttribute: props.defaultSortAttribute || null,
      sortAscending: props.defaultSortDirection !== descending,
      expandedParentRowIdentifier: '',
      hoveredColIndex: null
    }
  }

  componentDidMount() {
    this.checkForAggregateRowData()
  }

  handleMouseEnter = (colIndex) => {
    this.setState({ hoveredColIndex: colIndex });
  };

  handleMouseLeave = () => {
    this.setState({ hoveredColIndex: null });
  };


  // if there is aggregate row data, we want to automatically expand the first row
  checkForAggregateRowData() {
    const { rows, headers } = this.props
    if (rows?.length && headers?.length && rows[0].aggregate_rows?.length) {
      const aggregateRowIdentifier = `${rows[0][headers[0].attribute]}-${rows[0].id}`
      this.setState({ expandedParentRowIdentifier: aggregateRowIdentifier })
    }
  }


  handleHideAggregateRows = (e) => {
    e.preventDefault()
    // we only want to show aggregate data for one row at any given time, so we don't need to worry about keeping track of previously expanded rows
    this.setState({ expandedParentRowIdentifier: '' })
  }

  handleShowAggregateRows = (e, sectionText) => {
    e.preventDefault()
    this.setState({ expandedParentRowIdentifier: sectionText })
  }

  attributeAlignment(attributeName): CSS.TextAlignProperty {
    const { rows, } = this.props
    const numbersRegex = new RegExp(/^[\d#%\.\$]+$/)

    if (attributeName === 'username') { return left }

    return rows.every(row => numbersRegex.test(row[attributeName]) || !row[attributeName]) ? right : left
  }

  changeSort = (newSortAttribute) => {
    const { sortAttribute, } = this.state
    if (sortAttribute === newSortAttribute) {
      this.setState(prevState => ({ sortAscending: !prevState.sortAscending }))
    } else {
      this.setState({ sortAttribute: newSortAttribute, })
    }
  }

  sortRows() {
    const { sortAttribute, sortAscending, } = this.state
    const { rows } = this.props
    if (sortAttribute) {
      return rows.sort((a, b) => {
        if (a[sortAttribute] === b[sortAttribute]) { return 0; }
        if (a[sortAttribute] === null || a[sortAttribute] === undefined) { return 1; }
        if (b[sortAttribute] === null || b[sortAttribute] === undefined) { return -1; }
        if (sortAscending) { return a[sortAttribute] < b[sortAttribute] ? -1 : 1 }
        return a[sortAttribute] < b[sortAttribute] ? 1 : -1
      })
    }

    return rows
  }


  textAlign(header) {
    return header.textAlign || this.attributeAlignment(header.attribute)
  }

  renderHeaderCheckbox() {
    const { showCheckboxes, rows, uncheckAllRows, checkAllRows } = this.props

    const allDisabled = rows.every(row => row.checkDisabled)
    const allChecked = rows.every(row => row.checked)
    const anyChecked = rows.some(row => row.checked)

    const handleClick = allChecked || anyChecked ? uncheckAllRows : checkAllRows
    const ariaLabel = allChecked || anyChecked ? 'Unselect all rows' : 'Select all rows'
    let state = ''
    if (allDisabled) {
      state = DISABLED
    } else if (anyChecked && !allChecked) {
      state = INDETERMINATE
    }

    if (!showCheckboxes) { return }

    return (
      <th className={dataTableHeaderClassName} scope="col">
        <Checkbox
          ariaLabel={ariaLabel}
          onClick={handleClick}
          selected={allChecked}
          state={state}
        />
      </th>
    )
  }

  renderHeaderForRemoval() {
    const { showRemoveIcon, removeHeaderText, } = this.props
    if (!showRemoveIcon) { return null }

    return <th aria-label="Header for remove column" className={dataTableHeaderClassName} scope="col">{removeHeaderText}</th>
  }

  renderHeaderForOrder() {
    const { isReorderable } = this.props
    if (!isReorderable) { return }

    return <th className={`${dataTableHeaderClassName} reorder-header`} scope="col">Order</th>
  }

  renderActionsHeader(header, index) {
    const { showActions } = this.props
    if (!showActions) { return null }

    const ariaLabel = header.ariaLabel || header.name || 'Actions'

    return <th aria-label={ariaLabel} className={`${dataTableHeaderClassName} actions-header`} key={index} scope="col">{header.name || 'Actions'}</th>
  }

  renderRowCheckbox(row) {
    const { showCheckboxes, checkRow, uncheckRow } = this.props
    const handleClick = row.checked ? () => uncheckRow(row.id) : () => checkRow(row.id)
    const ariaLabel = row.checked ? `Unselect ${row.name || 'row'}` : `Select ${row.name || 'row'}`

    if (!showCheckboxes) { return null }

    return (
      <td key={row.id}>
        <Checkbox
          ariaLabel={ariaLabel}
          id={row.id}
          onClick={handleClick}
          selected={row.checked}
          state={row.checkDisabled ? DISABLED : ''}
        />
      </td>
    )
  }

  renderRowRemoveIcon(row) {
    const { showRemoveIcon, removeRow, } = this.props

    if (!showRemoveIcon) { return }

    if (row.removable) {
      return <td key={row.id}><button aria-label="Remove" className="removable data-table-row-section focus-on-light" id={`remove-button-${row.id}`} onClick={() => removeRow(row.id)} type="button"><img alt="x" src={removeSrc} /></button></td>
    }

    return <td className='removable data-table-row-section' key={row.id} />
  }

  renderActions(row) {
    if (!row.actions) {
      return (
        <td className="data-table-row-section actions-section" key={row.id}><p className="no-action">{NO_DATA}</p></td>
      )
    }

    const { rowWithActionsOpen } = this.state
    const isOpen = rowWithActionsOpen === row.id;

    return (
      <td className="data-table-row-section actions-section" key={row.id}>
        <ActionMenu
          actions={row.actions}
          id={row.id}
          isOpen={isOpen}
          onToggle={() => this.setState({ rowWithActionsOpen: isOpen ? null : row.id })}
        />
      </td>
    )
  }

  renderHeader(header, index) {
    if (header.isActions) { return this.renderActionsHeader(header, index) }

    const { sortAscending, sortAttribute, } = this.state

    let className = `${dataTableHeaderClassName} ${header.headerClassName}`
    const style: React.CSSProperties = { width: `${header.width}`, minWidth: `${header.width}`, textAlign: `${this.textAlign(header)}` as CSS.TextAlignProperty }
    let headerContent = <span>{header.name}</span>
    let headerTitle = <span>{header.name}</span>
    let headerTooltip = null
    const isMultilineHeader = header.primaryTitle && header.secondaryTitle

    const ariaLabel = header.ariaLabel || header.name

    if (header.isSortable && isMultilineHeader) {
      headerTitle = (
        <div className="multi-line-header">
          <span>{header.primaryTitle}</span>
          <span>{header.secondaryTitle}</span>
        </div>
      )
    } else if (isMultilineHeader) {
      headerContent = (
        <div className="multi-line-header">
          <span>{header.primaryTitle}</span>
          <span>{header.secondaryTitle}</span>
        </div>
      )
    }

    if (header.tooltipText || (header.tooltipName && header.tooltipDescription)) {
      headerTooltip = (
        <Tooltip
          skipPositioning={true}
          tooltipText={header.tooltipText ? header.tooltipText : `<span>${header.tooltipName}<br/><br/>${header.tooltipDescription}</span>`}
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />
      )
    }

    if (header.isSortable) {
      const sortDirection = sortAscending ? ascending : descending
      const sortChevronSrc = sortAscending ? sortChevronUpSrc : sortChevronDownSrc
      const onClick = () => this.changeSort(header.sortAttribute || header.attribute)
      const sortArrow = [header.attribute, header.sortAttribute].includes(sortAttribute) ? <img alt={`chevron to indicate sort ${sortDirection}`} className={`sort-arrow ${sortDirection}`} src={sortChevronSrc} /> : null
      className += ' sortable'
      headerContent = <button aria-label={`Sort column ${ariaLabel} in ${sortAscending ? descending : ascending} order`} className="interactive-wrapper focus-on-light" onClick={onClick} type="button">{headerTitle}<div className="sort-tooltip-container">{sortArrow}{!!headerTooltip && headerTooltip}</div></button>
      // resetting to not render tooltip twice
      headerTooltip = null
    }

    if (header.renderAsCell) {
      <td
        aria-label={ariaLabel}
        className={className}
        key={index}
        style={style as any}
      >
        {headerContent}
        {!!headerTooltip && headerTooltip}
      </td>
    }

    return (
      <th
        aria-label={ariaLabel}
        className={className}
        key={index}
        scope="col"
        style={style as any}
      >
        {headerContent}
        {!!headerTooltip && headerTooltip}
      </th>
    )
  }

  renderHeaders() {
    const { headers, } = this.props
    const headerItems = headers.map((header, index) => this.renderHeader(header, index))
    return (
      <thead>
        <tr className="data-table-headers">{this.renderHeaderCheckbox()}{this.renderHeaderForOrder()}{headerItems}{this.renderHeaderForRemoval()}</tr>
      </thead>
    )
  }

  renderRowSection({ row, header, isAggregateRow, i }) {
    const { expandedParentRowIdentifier } = this.state
    if (header.isActions) { return this.renderActions(row) }
    const { averageFontWidth, } = this.props
    const style: React.CSSProperties = { width: `${header.width}`, minWidth: `${header.width}`, textAlign: `${this.textAlign(header)}` as CSS.TextAlignProperty }
    const tooltipWidth = `${Number(header.width.split('px')[0] - 1)}px` // subtract 1 from header width to account for 1px right border
    const tooltipStyle: React.CSSProperties = { width: `${tooltipWidth}`, minWidth: `${tooltipWidth}`, textAlign: `${this.textAlign(header)}` as CSS.TextAlignProperty }
    const sectionText = row[header.attribute]

    const headerWidthNumber = Number(header.width.slice(0, -2))
    const dataTableRowSectionClassName = `data-table-row-section ${header.rowSectionClassName}`
    const key = `${header.attribute}-${row.id || sectionText}`

    let className = `data-table-row-section ${header.rowSectionClassName}`;
    if (this.state.hoveredColIndex === i) {
      className += ' highlight-column';
    }

    // Extract content from React elements (handles parsed HTML like <em>, <strong>)
    // - reactElementToHtml preserves formatting for tooltips
    // - extractTextFromReactElement returns plain text for aria-labels
    let linkDisplayHtml
    if (sectionText && sectionText.type === 'a' && sectionText.props.children && sectionText.props.children[1] && sectionText.props.children[1].props) {
      linkDisplayHtml = reactElementToHtml(sectionText.props.children[1].props.children)
    } else if (sectionText && sectionText.type && sectionText.type.displayName === 'Link' && sectionText.props && sectionText.props.children) {
      linkDisplayHtml = reactElementToHtml(sectionText.props.children)
    }

    const labelText = extractTextFromReactElement(sectionText)
    const rowDisplayText = linkDisplayHtml || reactElementToHtml(sectionText)
    const aggregateRowIdentifier = `${sectionText}-${row.id}`
    const shouldDisplayAggregateIcon = isAggregateRow && i === 0
    const shouldDisplayToggleButton = row.aggregate_rows && row.aggregate_rows.length && i === 0
    const aggregateRowsDisplayed = expandedParentRowIdentifier && expandedParentRowIdentifier === aggregateRowIdentifier

    // Wrapper function that wraps content inside the cell (not the cell itself)
    let renderCellContent = (content) => content

    if (shouldDisplayAggregateIcon) {
      renderCellContent = (content) => (
        <span className="aggregate-row-content">
          <img alt="" className="aggregate-row-icon" src={aggregateRowArrowSrc} />
          {content}
        </span>
      )
    } else if (shouldDisplayToggleButton && aggregateRowsDisplayed) {
      renderCellContent = (content) => (
        <button aria-label={`hide aggregate row data for ${labelText}`} className="aggregate-row-toggle interactive-wrapper focus-on-light" onClick={this.handleHideAggregateRows} type="button">
          <img alt="" className="aggregate-row-toggle-icon" src={toggleArrowExpandedSrc} />
          <span className="aggregate-row-toggle-content">{content}</span>
        </button>
      )
    } else if (shouldDisplayToggleButton) {
      renderCellContent = (content) => (
        <button aria-label={`show aggregate row data for ${labelText}`} className="aggregate-row-toggle interactive-wrapper focus-on-light" onClick={(e) => this.handleShowAggregateRows(e, aggregateRowIdentifier)} type="button">
          <img alt="" className="aggregate-row-toggle-icon" src={toggleArrowClosedSrc} />
          <span className="aggregate-row-toggle-content">{content}</span>
        </button>
      )
    }

    if (!header.noTooltip && (labelText.length * averageFontWidth) >= headerWidthNumber) {
      const cellContent = renderCellContent(
        <Tooltip
          key={key}
          tooltipText={rowDisplayText}
          tooltipTriggerStyle={tooltipStyle}
          tooltipTriggerText={sectionText}
          tooltipTriggerTextClass={dataTableRowSectionClassName}
          tooltipTriggerTextStyle={tooltipStyle}
        />
      )
      return header.renderCellsAsHeaders ? <th key={key}>{cellContent}</th> : <td key={key}>{cellContent}</td>
    } else if (header.containsOwnTooltip) {
      return (
        <td
          className={className}
          key={key}
          style={style as any}
        >
          {renderCellContent(sectionText)}
        </td>
      )
    } else if (header.renderCellsAsHeaders) {
      // ignoring this linter rule because the interactive effects are only for visual changes (we add a class name)
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      return (
        <th
          className={className}
          key={key}
          onMouseEnter={() => this.handleMouseEnter(i)}
          onMouseLeave={this.handleMouseLeave}
          scope="row"
          style={style as any}
        >
          {renderCellContent(sectionText)}
        </th>
      )
      /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
    } else {
      // ignoring this linter rule because the interactive effects are only for visual changes (we add a class name)
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      return (
        <td
          className={className}
          key={key}
          onMouseEnter={() => this.handleMouseEnter(i)}
          onMouseLeave={this.handleMouseLeave}
          style={style as any}
        >
          {renderCellContent(sectionText)}
        </td>
      )
      /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
    }
  }

  handleRowClick(row) {
    if (!row.link) { return }

    window.location = row.link
  }

  renderRowDragHandle(row) {
    const { isReorderable, reportingFormat, } = this.props
    if (!isReorderable) { return }

    const className = "reorder-section data-table-row-section"

    if (!row.id) {
      return <td className={className} />
    }

    const imageSrc = reportingFormat ? extraSmallReorderSrc : reorderSrc
    return (
      <td className={className} >
        <DragHandle>
          <img alt="Reorder icon" className="reorder-icon" src={imageSrc} />
        </DragHandle>
      </td>
    )
  }

  renderRow(row, isAggregateRow = false) {
    const { expandedParentRowIdentifier } = this.state
    const { headers, } = this.props
    const rowClassName = `data-table-row ${row.checked ? 'checked' : ''} ${row.className || ''} ${row.link ? 'show-pointer' : ''}`
    const rowSections = headers.map((header, i) => this.renderRowSection({ row, header, isAggregateRow, i }))
    const rowContent = <React.Fragment>{this.renderRowCheckbox(row)}{this.renderRowDragHandle(row)}{rowSections}{this.renderRowRemoveIcon(row)}</React.Fragment>
    const aggregateRowClassName = isAggregateRow ? 'aggregate-row' : ''
    let rowElement = <tr className={`${rowClassName}${aggregateRowClassName}`} key={String(row.id)} onClick={() => this.handleRowClick(row)}>{rowContent}</tr>
    const aggregateRowIdentifier = `${row[headers[0].attribute]}-${row.id}`
    const showAggregateRows = row.aggregate_rows?.length && expandedParentRowIdentifier === aggregateRowIdentifier
    const showEmbeddedTable = row.renderEmbeddedTableFunction && expandedParentRowIdentifier === aggregateRowIdentifier

    if (showEmbeddedTable) {
      return (
        <React.Fragment key={String(row.id)}>
          {rowElement}
          {row.renderEmbeddedTableFunction(row.aggregate_rows)}
        </React.Fragment>
      )
    }

    if (showAggregateRows) {
      return (
        <React.Fragment key={String(row.id)}>
          {rowElement}
          {row.aggregate_rows.map(row => {
            return this.renderRow(row, true)
          })}
        </React.Fragment>
      )
    }

    return rowElement
  }

  renderRows() {
    const { isReorderable, reorderCallback, emptyStateMessage, headers, sortIsDisabled, overlayInPortal, overlayWrapper } = this.props
    const rows = this.sortRows().map(row => this.renderRow(row))
    const contentToDisplay = emptyStateMessage && !rows.length ? <tbody><tr><td className="empty-state-message" colSpan={headers.length}>{emptyStateMessage}</td></tr></tbody> : rows
    if (isReorderable) {
      return (
        <tbody className="data-table-body reorderable">
          <SortableList data={rows} disabled={sortIsDisabled} helperClass="sortable-data-table-row" overlayInPortal={overlayInPortal} overlayWrapper={overlayWrapper} sortCallback={reorderCallback} useDragHandle={true} />
        </tbody>
      )
    }
    return <tbody className="data-table-body">{contentToDisplay}</tbody>
  }

  render() {
    const { className, reportingFormat, tableAriaLabel, tableRef, } = this.props
    const reportingFormatClassName = reportingFormat ? ' reporting-format' : ''
    return (
      <table aria-label={tableAriaLabel} className={`data-table ${className}${reportingFormatClassName}`} ref={tableRef}>
        {this.renderHeaders()}
        {this.renderRows()}
      </table>
    )
  }

}

// TODO: refactor DataTable into a functional component so this defaultProps can be inlined as a destructured default. defaultProps on class components still works in React 19 but is slated for removal.
DataTable.defaultProps = {
  averageFontWidth: 7
}

export default DataTable
